import React, { FC } from 'react'
import { getTrackingMethodName } from 'components/TrackingContextCard/utils'
import { FormattedDateTime } from 'transfix-ui/components/FormattedDateTime'
import { Tooltip } from 'transfix-ui/components/Tooltip'
import { IShipmentStates } from 'types/graphqlTypes'
import { TrackingIcon, InfoIcon } from 'transfix-icons'
import { currentTimezone } from 'constants/AllowedTimezones'
import LocationStat, { TStatStyle } from './LocationStat'
import { useTrackingInfoQuery } from './graphql/TrackingInfo'
import { getLocationTextByStatus } from './utils'

interface ILocationDisplayProps {
  facilityName: string
  note: string
  shipmentId: string
  shipmentState: IShipmentStates | undefined
  shipmentUpdatedAt: string
  showMissingTrailers: boolean
  stopName: string
  trailerState: string
  trailerUpdatedAt: string
}

const LOCATION_STATS = {
  not_tracking_not_required: {
    text: 'Not tracking',
    color: 'dark',
  },
  not_tracking_required: {
    text: 'Not tracking \u2022 Tracking Required',
    color: 'error',
  },
  currently_tracking: {
    text: 'Currently Tracking',
    color: 'info',
  },
  was_tracking: {
    text: 'Was Tracking',
    color: 'warning',
  },
}

const LocationDisplay: FC<ILocationDisplayProps> = ({
  facilityName,
  note,
  shipmentId,
  shipmentState,
  shipmentUpdatedAt,
  showMissingTrailers,
  stopName,
  trailerState,
  trailerUpdatedAt,
}) => {
  const { data } = useTrackingInfoQuery({
    variables: {
      id: shipmentId || '',
    },
    skip: !shipmentId,
  })

  const { trackingOverview } = data || {}
  const { calculatedTrackingMethod, trackingState } = trackingOverview || {}
  const { color, text } = (trackingState &&
    LOCATION_STATS[trackingState as keyof typeof LOCATION_STATS]) || {
    color: 'default',
    text: 'Not Tracking',
  }
  const trackingMethod = getTrackingMethodName(calculatedTrackingMethod)

  const trailerStatus = getLocationTextByStatus(
    trailerState,
    facilityName,
    shipmentState,
    stopName,
    showMissingTrailers
  )

  return (
    <>
      <Tooltip trigger={<div>{trailerStatus}</div>}>
        As of{' '}
        <FormattedDateTime
          datetime={!showMissingTrailers ? trailerUpdatedAt : shipmentUpdatedAt}
          format='T24TimeDateAndYearSecondary'
          displayTimezone={currentTimezone}
        />
      </Tooltip>
      {trackingState && (
        <Tooltip
          trigger={
            <div>
              <LocationStat statStyle={color as TStatStyle} icon={<TrackingIcon />} />
            </div>
          }>
          {trackingMethod}: {text}
        </Tooltip>
      )}
      {note && <Tooltip trigger={<InfoIcon size='large' />}>{note}</Tooltip>}
    </>
  )
}

export default LocationDisplay
